import * as React from "react";

import cn from "classnames";
import { generatePath, Link, useNavigate, useParams } from "react-router-dom";
import Styles from "./ScheduleList.module.scss";
import { UUID } from "src/utils/uuid";
import { EventID, GameState } from "src/state/modules/event/types/types";
import { Text } from "src/components/atoms/Text/Text";
import { EventScore } from "src/components/atoms/EventScore/EventScore";
import { Icon } from "src/components/atoms/Icon/Icon";
import { OldRow } from "src/components/atoms/OldGrid/OldRow";
import { Toast, ToastTone } from "src/components/atoms/Toast/Toast";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import { ROUTES } from "src/utils/constants/routes";
import { dateComparator, getNow } from "src/utils/date-utils";
import {
  useAddTeamByIDMutation,
  useGetMeTeamsQuery,
  useGetTeamEventAndSummariesByIDQuery,
  useGetTeamLiveGamesByIDQuery,
  useGetTeamOpponentsByIDQuery,
  useUnfollowTeamMutation,
  useFollowTeamPrivateMutation,
} from "src/state/modules/api";

// Background-import progress for an opponent, keyed by progenitor (the opponent's real team id).
type ImportState = "importing" | "done" | "error";

export interface EventForDay<T extends UUID<string> = EventID> {
  id: T;
  title: string;
  location: string;
  time: string;
  score?: string;
  eventPath: string;
  status: undefined | GameState;
  isScrimmage: boolean;
  isCanceled?: boolean;
  progenitorID?: string | undefined;
  isNext?: boolean;
}

export interface ScheduleDataDay<T extends UUID<string> = EventID> {
  day: string;
  date: string;
  events: EventForDay<T>[];
  children?: React.ReactNode;
}

export interface ScheduleData<T extends UUID<string> = EventID> {
  monthYear: string;
  monthAbbreviation: string;
  days: ScheduleDataDay<T>[];
  children?: React.ReactNode;
}

export interface EventMonthData {
  monthYear: string;
  children?: React.ReactNode;
}

export interface ListData<T extends UUID<string>> {
  scheduleData: ScheduleData<T>[];
}

/** Walks up from `el` to the nearest vertically scrollable ancestor (the page's
 *  `.mainGrid`), so we can scroll *that* container — keeping its sticky TeamNavBar
 *  pinned at the top, which scrollIntoView() doesn't guarantee. */
const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const GameStatus = ({ gameState }: { gameState: GameState | undefined }) => {
  if (gameState && gameState !== GameState.GAME_OVER) {
    return (
      <Text block className={Styles.gameStatusLive}>
        LIVE
      </Text>
    );
  }
  return <></>;
};

interface EventProps<T extends UUID<string>> {
  event: EventForDay<T>;
  /** The opponent is already in the user's teams (or just finished importing). */
  imported: boolean;
  importState?: ImportState;
  onImport: (progenitorID: string, label: string) => void;
}

function Event<T extends UUID<string>>({ event, imported, importState, onImport }: EventProps<T>) {
  const eventRef = useRef<HTMLAnchorElement>(null);
  const importing = importState === "importing";

  useEffect(() => {
    if (!event.isNext) return;
    const el = eventRef.current;
    if (!el) return;

    const container = getScrollParent(el);
    if (!container) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    // Center the event within its scroll container. Scrolling the container
    // directly (rather than scrollIntoView, which may scroll an outer ancestor)
    // keeps the sticky TeamNavBar pinned at the top.
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const target = container.scrollTop + (elRect.top - containerRect.top) - container.clientHeight / 2 + elRect.height / 2;
    container.scrollTo({ top: Math.max(target, 0), behavior: "smooth" });
  }, [event.isNext]);

  const handleImportClick = (e: SyntheticEvent) => {
    // The chip lives inside the event's <Link>; stop the click from navigating into the event
    // and just kick off the background import.
    e.preventDefault();
    e.stopPropagation();
    onImport(event.progenitorID ?? "", event.title);
  };

  return (
    <Link ref={eventRef} className={Styles.event} to={event.eventPath}>
      <div className={Styles.title}>
        {event.isCanceled ? (
          <Text block className={Styles.gameStatusCanceled}>
            CANCELED
          </Text>
        ) : (
          <GameStatus gameState={event.status} />
        )}
        <Text className={cn(Styles.titleName, event.isCanceled && Styles.canceledText)} weight={"semibold"}>
          {event.title}
        </Text>
        {importing ? (
          <span className={cn(Styles.import, Styles.importing)} aria-live="polite">
            <span className={Styles.importSpinner} aria-hidden="true" />
            Importing…
          </span>
        ) : event.progenitorID ? (
          imported ? (
            <Link to={generatePath(ROUTES.teams.home, { id: event.progenitorID })} className={Styles.imported}>
              View
            </Link>
          ) : (
            <span onClick={handleImportClick} className={Styles.import}>
              <Icon type={"plus"} size={"xsmall"} fill={"gcBlue"} />
              Import
            </span>
          )
        ) : (
          ""
        )}
      </div>
      <Text block size={"small"} color={"coolGreyDark"} className={Styles.location}>
        {event.location}
      </Text>
      <div className={Styles.scoreOrTime}>
        {event.score && !event.isCanceled ? (
          <EventScore className={Styles.scoreOrTimeText} score={event.score} />
        ) : (
          <Text className={cn(Styles.time, Styles.scoreOrTimeText, event.isCanceled && Styles.canceledText)}>
            {event.time}
          </Text>
        )}
        <span className={Styles.chevron}>
          <Icon type={"chevron"} size={"xsmall"} fill={"coolGreyDark"} />
        </span>
      </div>
    </Link>
  );
}

function EventDay<T extends UUID<string>>({ day, date, children }: ScheduleDataDay<T>) {
  return (
    <div className={Styles.dayRow}>
      <div className={Styles.dayDate}>
        <Text block size={"small"} color={"coolGreyDark"} align={"center"}>
          {day}
        </Text>
        <Text block className={Styles.dateText} weight={"xbold"} align={"center"}>
          {date}
        </Text>
      </div>
      <div>{children}</div>
    </div>
  );
}

const EventMonth = ({ monthYear, children }: EventMonthData) => {
  return (
    <>
      <OldRow padded={false} className={cn(Styles.monthHeader)}>
        <Text weight={"xbold"} className={Styles.monthYear}>
          {monthYear}
        </Text>
      </OldRow>
      <div className={Styles.eventMonth}>{children}</div>
    </>
  );
};

export function ScheduleList<T extends UUID<string>>({ scheduleData }: ListData<T>) {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: schedule, isLoading: scheduleIsLoading } = useGetTeamEventAndSummariesByIDQuery(id ?? "");
  const { data: opponents, isLoading: opponentsLoading } = useGetTeamOpponentsByIDQuery(id ?? "");
  const { data: liveGames } = useGetTeamLiveGamesByIDQuery(id ?? "");
  const { data: teams, isLoading: teamsLoading } = useGetMeTeamsQuery(null);

  const [addTeam] = useAddTeamByIDMutation();
  const [followTeamPrivate] = useFollowTeamPrivateMutation();
  const [unfollowTeam] = useUnfollowTeamMutation();

  // Import progress lives here (not per-row) so it survives re-renders, the import keeps running
  // in the background while the user browses, and every game vs the same opponent updates at once.
  const [importStatus, setImportStatus] = useState<Record<string, ImportState>>({});
  const [toast, setToast] = useState<{ tone: ToastTone; message: string; teamId?: string } | null>(null);
  // Synchronous single-fire guard: state updates are async, so two fast taps could both pass the
  // status check before the first re-render. The ref blocks the second one immediately.
  const inFlight = useRef<Set<string>>(new Set());

  const handleImport = async (progenitorID: string, label: string) => {
    if (!progenitorID) return;
    if (inFlight.current.has(progenitorID)) return;

    inFlight.current.add(progenitorID);
    setImportStatus((prev) => ({ ...prev, [progenitorID]: "importing" }));
    setToast({ tone: "loading", message: `Importing ${label}…` });

    try {
      // Follow → add → unfollow, each fully awaited (the old code used `await setTimeout(...)`,
      // which doesn't wait, so it reported "done" before the import had actually run).
      await followTeamPrivate(progenitorID).unwrap();
      await addTeam(progenitorID).unwrap();
      await unfollowTeam(progenitorID).unwrap();
      setImportStatus((prev) => ({ ...prev, [progenitorID]: "done" }));
      setToast({ tone: "success", message: `${label} imported`, teamId: progenitorID });
    } catch (e) {
      console.error("Failed to import opponent team", e);
      setImportStatus((prev) => ({ ...prev, [progenitorID]: "error" }));
      setToast({ tone: "error", message: `Couldn't import ${label}` });
    } finally {
      inFlight.current.delete(progenitorID);
    }
  };

  if (opponentsLoading || scheduleIsLoading || teamsLoading) return <PageLoader1 />;
  if (schedule === undefined || opponents === undefined) return <PageLoader1 />;

  // Scroll target priority: a game that's in progress right now (so the live game is
  // centered instead of scrolled past), then the first event that hasn't started yet,
  // then the most recent past event so the list still centers somewhere sensible.
  const now = getNow();
  const sortedByStart = [...schedule].sort((a, b) => dateComparator(a.start, b.start));
  const liveEventIds = new Set((liveGames ?? []).filter((g) => g.is_live).map((g) => g.event_id));
  const liveEvent = sortedByStart.find(
    (evt) => liveEventIds.has(evt.id) || evt.gameData?.game_state === GameState.LIVE
  );
  const nextEvent =
    liveEvent ??
    sortedByStart.find((evt) => evt.status !== "canceled" && dateComparator(evt.start, now) >= 0) ??
    sortedByStart.at(-1);
  const nextEventId = nextEvent?.id;

  const getProgenitorId = (eventID: string) => {
    const curEvent = schedule.find((evt) => {
      return evt.id === eventID;
    });
    const result = opponents.find((opp) => {
      return opp.root_team_id === curEvent?.opponentTeam?.id;
    });
    return result?.progenitor_team_id;
  };

  const importedTeamIds = new Set<string>((teams ?? []).map((team) => team.id));

  return (
    <>
      <OldRow>
        {scheduleData.map(({ monthYear, days }, i) => {
          if (!days && !monthYear) return null;

          return (
            <EventMonth key={i} monthYear={monthYear}>
              {days.map((day, i) => (
                <EventDay key={i} {...day}>
                  {day.events.map((event, j) => {
                    const progenitorID = getProgenitorId(event.id ?? "") ?? "";
                    const tmp = {
                      ...event,
                      progenitorID,
                      isNext: event.id === nextEventId,
                    };
                    const status = importStatus[progenitorID];
                    const imported = !!progenitorID && (importedTeamIds.has(progenitorID) || status === "done");
                    return (
                      <Event key={j} event={tmp} imported={imported} importState={status} onImport={handleImport} />
                    );
                  })}
                </EventDay>
              ))}
            </EventMonth>
          );
        })}
      </OldRow>
      {toast && (
        <Toast
          tone={toast.tone}
          message={toast.message}
          duration={toast.tone === "loading" ? undefined : 6000}
          onDismiss={() => setToast(null)}
          actionLabel={toast.teamId ? "View" : undefined}
          onAction={toast.teamId ? () => navigate(generatePath(ROUTES.teams.home, { id: toast.teamId! })) : undefined}
        />
      )}
    </>
  );
}
